/* Design System PASCOM — componentes base (Fase 2). Headless accesible vía Radix donde aporta comportamiento. */
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import * as Toast from '@radix-ui/react-toast';
import s from './ui.module.css';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------- Button
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'md' | 'sm'; block?: boolean; loading?: boolean };
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'primary', size = 'md', block, loading, className, children, disabled, ...rest }, ref) {
  return (
    <button ref={ref} className={cx(s.button, variant !== 'primary' && s[variant], size === 'sm' && s.small, block && s.block, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? 'Procesando…' : children}
    </button>
  );
});

// ---------------------------------------------------------------- Input / Field
type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; mono?: boolean };
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ invalid, mono, className, ...rest }, ref) {
  return <input ref={ref} className={cx(s.input, invalid && s.inputInvalid, mono && s.inputMono, className)} aria-invalid={invalid || undefined} {...rest} />;
});

export function Field({ label, htmlFor, hint, error, children }: { label: string; htmlFor: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <div className={s.error} role="alert" id={`${htmlFor}-error`}>{error}</div> : hint ? <div className={s.hint} id={`${htmlFor}-hint`}>{hint}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Card
export function Card({ title, children, className, actions }: { title?: string; children: ReactNode; className?: string; actions?: ReactNode }) {
  return (
    <section className={cx(s.card, className)}>
      {(title || actions) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {title && <h2 className={s.cardTitle}>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------- Badge / StatusBadge
export type Tone = 'neutral' | 'pending' | 'review' | 'ok' | 'observed' | 'rejected' | 'overdue';
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={s.badge} style={{ color: `var(--tone-${tone}-fg)`, background: `var(--tone-${tone}-bg)` }}>{children}</span>;
}

/** Mapa de estados de negocio (nombres definitivos del modelo de datos) → etiqueta y tono. */
export const STATUS_LABELS: Record<string, { label: string; tone: Tone }> = {
  BORRADOR: { label: 'Borrador', tone: 'neutral' },
  PENDIENTE_GERENCIA: { label: 'Pendiente Gerencia', tone: 'pending' },
  PENDIENTE_FINANZAS: { label: 'Pendiente Finanzas', tone: 'pending' },
  FONDOS_TRANSFERIDOS: { label: 'Fondos transferidos', tone: 'ok' },
  RECHAZADA: { label: 'Rechazada', tone: 'rejected' },
  CANCELADA: { label: 'Cancelada', tone: 'neutral' },
  ENVIADO: { label: 'Enviado', tone: 'review' },
  OBSERVADO_COORDINADOR: { label: 'Observado (Coordinador)', tone: 'observed' },
  EN_REVISION_FINANZAS: { label: 'En revisión Finanzas', tone: 'review' },
  OBSERVADO_FINANZAS: { label: 'Observado (Finanzas)', tone: 'observed' },
  APROBADO: { label: 'Aprobado', tone: 'ok' },
  RECHAZADO: { label: 'Rechazado', tone: 'rejected' },
  REABIERTO: { label: 'Reabierto', tone: 'pending' },
  PENDIENTE_CONFIRMACION: { label: 'Pendiente de confirmación', tone: 'pending' },
  LISTO: { label: 'Listo', tone: 'ok' },
  OBSERVADO: { label: 'Observado', tone: 'observed' },
  CERRADO: { label: 'Cerrado', tone: 'neutral' },
  PENDIENTE: { label: 'Pendiente', tone: 'pending' },
  PROCESANDO: { label: 'Procesando', tone: 'review' },
  PROCESADO: { label: 'Leído automáticamente', tone: 'ok' },
  REQUIERE_REVISION: { label: 'Revisa estos datos', tone: 'observed' },
  ERROR: { label: 'No se pudo leer', tone: 'rejected' },
  DESHABILITADO: { label: 'Lectura automática desactivada', tone: 'neutral' },
  VENCIDO: { label: 'Vencido', tone: 'overdue' },
  EN_PROCESO: { label: 'En proceso', tone: 'review' },
  REEMBOLSADO: { label: 'Reembolsado', tone: 'ok' },
  SOLICITADA: { label: 'Solicitada', tone: 'pending' },
  PARCIAL: { label: 'Parcial', tone: 'review' },
  CONFIRMADA: { label: 'Confirmada', tone: 'ok' },
  PENDING: { label: 'Pendiente', tone: 'pending' },
  SENDING: { label: 'Enviando', tone: 'review' },
  SENT: { label: 'Enviado', tone: 'ok' },
  FAILED: { label: 'Fallido', tone: 'rejected' },
  NETEADO: { label: 'Neteado', tone: 'neutral' },
  NETEADA: { label: 'Neteada', tone: 'neutral' },
  ANULADO: { label: 'Anulado', tone: 'neutral' },
  ANULADA: { label: 'Anulada', tone: 'neutral' },
  APROBADA: { label: 'Aprobada', tone: 'ok' },
  CORRIENDO: { label: 'Reloj corriendo', tone: 'pending' },
  DETENIDO: { label: 'Reloj detenido', tone: 'review' },
  ACTIVA: { label: 'Activa', tone: 'ok' },
  REINTENTAR: { label: 'Reintentar', tone: 'pending' },
  COMPLETADO: { label: 'Completado', tone: 'ok' },
  FALLIDO: { label: 'Fallido', tone: 'rejected' },
  NO_APLICA: { label: 'Carga manual', tone: 'neutral' },
};
export function StatusBadge({ status }: { status: string }) {
  const m = STATUS_LABELS[status] ?? { label: status, tone: 'neutral' as Tone };
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

// ---------------------------------------------------------------- Alert
export function Alert({ kind = 'info', children }: { kind?: 'info' | 'error' | 'warning' | 'success'; children: ReactNode }) {
  const cls = { info: s.alertInfo, error: s.alertError, warning: s.alertWarning, success: s.alertSuccess }[kind];
  return <div className={cx(s.alert, cls)} role={kind === 'error' ? 'alert' : 'status'}>{children}</div>;
}

// ---------------------------------------------------------------- Skeleton / EmptyState
export function Skeleton({ height = 16, width = '100%' }: { height?: number; width?: string | number }) {
  return <div className={s.skeleton} style={{ height, width }} aria-hidden="true" />;
}
export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      <div className={s.emptyTitle}>{title}</div>
      {description && <div>{description}</div>}
      {action && <div style={{ marginTop: 'var(--sp-4)' }}>{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- Table
export function Table({ children }: { children: ReactNode }) {
  return <div className={s.tableWrap}><table className={s.table}>{children}</table></div>;
}

// ---------------------------------------------------------------- UserAvatar
export function UserAvatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
  return <span className={s.avatar} aria-hidden="true">{initials || '?'}</span>;
}

// ---------------------------------------------------------------- Toast (Radix)
export type ToastMsg = { id: number; title: string; description?: string; kind?: 'info' | 'error' };
export function ToastStack({ toasts, onDismiss }: { toasts: ToastMsg[]; onDismiss: (id: number) => void }) {
  return (
    <Toast.Provider swipeDirection="right" duration={6000}>
      {toasts.map((t) => (
        <Toast.Root key={t.id} className={cx(s.toast, t.kind === 'error' && s.toastError)} onOpenChange={(open) => !open && onDismiss(t.id)}>
          <div>
            <Toast.Title className={s.toastTitle}>{t.title}</Toast.Title>
            {t.description && <Toast.Description>{t.description}</Toast.Description>}
          </div>
          <Toast.Close className={s.toastClose} aria-label="Cerrar">×</Toast.Close>
        </Toast.Root>
      ))}
      <Toast.Viewport className={s.toastViewport} />
    </Toast.Provider>
  );
}
